import React, { useEffect, useState } from "react";
import {
  getPartnerPropertiesRequest,
  getPartnerPropertiesReset,
  createPartnerPropertyRequest,
  createPartnerPropertyReset,
  updatePartnerPropertyRequest,
  updatePartnerPropertyReset,
  downloadKeyRequest,
} from "../../../store/actions";
import { connect, useDispatch } from "react-redux";
import { LoadingView, PageTitle } from "../../../components";
import { useLocation } from "react-router-dom";
import { STATUS } from "../../../constant";
import {
  Box,
  Sheet,
  Table,
  Typography,
  Button,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Select,
  Option,
} from "@mui/joy";
import { toast } from "react-toastify";
import { Edit, FileDownload } from "@mui/icons-material";

const PROPERTY_NAME_OPTIONS = [
  "base_url",
  "login_url",
  "complete_withdraw_url",
  "customer_account_lookup_url",
  "retrieve_agent_details_url",
  "transaction_status_url",
  "complete_deposit_url",
  "retrieve_agent_transactions_url",
];

function PartnerPropertiesPage({
  getPartnerPropertiesStatus,
  getPartnerPropertiesResult,
  getPartnerPropertiesErrorMessage,

  createPartnerPropertyStatus,
  createPartnerPropertyResult,
  createPartnerPropertyErrorMessage,

  updatePartnerPropertyStatus,
  updatePartnerPropertyResult,
  updatePartnerPropertyErrorMessage,

  downloadKeyStatus,
  downloadKeyResult,
  downloadKeyErrorMessage,

  loginResult,
}) {
  const location = useLocation();
  const dispatch = useDispatch();
  const fsp_code = location.state?.fsp_code;
  let hasDownloaded = false;

  const [addPropertyModal, setAddPropertyModal] = useState(false);
  const [propertyName, setPropertyName] = useState("");
  const [propertyValue, setPropertyValue] = useState("");
  const [downloadFileName, setDownloadFileName] = useState("key");

  const [editPropertyModal, setEditPropertyModal] = useState(false);
  const [editPropertyName, setEditPropertyName] = useState("");
  const [editPropertyValue, setEditPropertyValue] = useState("");
  const [editPropertyId, setEditPropertyId] = useState(null);

  const normalizePropertyName = (value) =>
    String(value ?? "").trim().toLowerCase();

  const existingPropertyNames = new Set(
    (getPartnerPropertiesResult ?? []).map((row) =>
      normalizePropertyName(row.property_name),
    ),
  );

  // Only offer property names that have not been added to this partner yet.
  const availablePropertyOptions = PROPERTY_NAME_OPTIONS.filter(
    (name) => !existingPropertyNames.has(normalizePropertyName(name)),
  );

  // Keep every known property name, except the ones already used by another row.
  // The row's own name is always kept (so it can be selected back) and any
  // legacy/system name (e.g. private_key) still renders in the Select.
  const editingRow = (getPartnerPropertiesResult ?? []).find(
    (row) => row.id === editPropertyId,
  );
  const ownedPropertyName = editingRow?.property_name ?? "";

  const editPropertyOptions = Array.from(
    new Set(
      [...PROPERTY_NAME_OPTIONS, ownedPropertyName, editPropertyName].filter(
        Boolean,
      ),
    ),
  ).filter(
    (name) =>
      normalizePropertyName(name) === normalizePropertyName(ownedPropertyName) ||
      normalizePropertyName(name) ===
        normalizePropertyName(editPropertyName) ||
      !existingPropertyNames.has(normalizePropertyName(name)),
  );

  const handleAddProperty = (e) => {
    e.preventDefault();
    const data = {
      fsp_code: fsp_code,
      property_name: propertyName,
      property_value: propertyValue,
    };
    dispatch(createPartnerPropertyRequest(loginResult?.token, data));
    setAddPropertyModal(false);
    setPropertyName("");
    setPropertyValue("");
  };

  const downloadKey = (id, property_name) => {
    dispatch(downloadKeyRequest(loginResult?.token, { id: Number(id) }));
    setDownloadFileName(property_name); // store it for when the download triggers
  };

  const handleEditProperty = (e) => {
    e.preventDefault();
    const data = {
      fsp_code: fsp_code,
      id: editPropertyId,
      property_name: editPropertyName,
      property_value: editPropertyValue,
    };
    dispatch(updatePartnerPropertyRequest(loginResult?.token, data));
    setEditPropertyModal(false);
    setEditPropertyName("");
    setEditPropertyValue("");
    setEditPropertyId(null);
  };

  const checkLoading = () => {
    if (getPartnerPropertiesStatus === STATUS.LOADING) {
      return true;
    } else if (createPartnerPropertyStatus === STATUS.LOADING) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    const data = {
      fsp_code: fsp_code,
    };

    dispatch(getPartnerPropertiesRequest(loginResult?.token, data));
  }, [dispatch, fsp_code, loginResult]);

  useEffect(() => {
    if (getPartnerPropertiesStatus === STATUS.ERROR) {
      toast.error(getPartnerPropertiesErrorMessage, {
        toastId: "getPartnerPropertiesError",
      });

      dispatch(getPartnerPropertiesReset());
    }

    if (createPartnerPropertyStatus === STATUS.ERROR) {
      toast.error(createPartnerPropertyErrorMessage, {
        toastId: "createPartnerPropertyError",
      });

      dispatch(createPartnerPropertyReset());
    }

    if (createPartnerPropertyStatus === STATUS.SUCCESS) {
      toast.success("Partner property created successfully", {
        toastId: "createPartnerPropertySuccess",
      });

      dispatch(createPartnerPropertyReset());
      const data = {
        fsp_code: fsp_code,
      };

      dispatch(getPartnerPropertiesRequest(loginResult?.token, data));
    }

    if (updatePartnerPropertyStatus === STATUS.ERROR) {
      toast.error(updatePartnerPropertyErrorMessage, {
        toastId: "updatePartnerPropertyError",
      });

      dispatch(updatePartnerPropertyReset());
    }

    if (updatePartnerPropertyStatus === STATUS.SUCCESS) {
      toast.success("Partner property updated successfully", {
        toastId: "updatePartnerPropertySuccess",
      });

      dispatch(updatePartnerPropertyReset());
      const data = {
        fsp_code: fsp_code,
      };

      dispatch(getPartnerPropertiesRequest(loginResult?.token, data));
    }

    if (downloadKeyStatus === STATUS.ERROR) {
      toast.error(
        "Cannot download this! The file is not found or it is not a key",
        {
          toastId: "downloadKeyError",
        },
      );
    }

    if (downloadKeyStatus === STATUS.SUCCESS && !hasDownloaded) {
      hasDownloaded = true;

      const blob = new Blob([downloadKeyResult], {
        type: "application/x-pem-file",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `${downloadFileName}.pem`; // ✅ uses property name

      document.body.appendChild(link);

      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  }, [
    dispatch,
    getPartnerPropertiesErrorMessage,
    getPartnerPropertiesStatus,
    createPartnerPropertyStatus,
    createPartnerPropertyErrorMessage,
    fsp_code,
    loginResult,
    updatePartnerPropertyStatus,
    updatePartnerPropertyErrorMessage,
    downloadKeyStatus,
    downloadKeyResult,
  ]);

  return (
    <Box>
      <PageTitle title={"Partner Properties"} />

      <LoadingView loading={checkLoading()} />

      {/* Action Button */}
      <Box
        sx={{
          mb: 3,
          mx: 2,
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Button
          color="primary"
          onClick={() => {
            setPropertyName("");
            setPropertyValue("");
            setAddPropertyModal(true);
          }}
        >
          Add Property
        </Button>
      </Box>

      <Box>
        <Sheet
          variant="soft"
          sx={{
            borderRadius: "sm",
            overflow: "auto",
            mt: 2,
          }}
        >
          <Table
            stickyHeader
            hoverRow
            sx={{
              "--TableCell-headBackground":
                "var(--joy-palette-background-level2)",
              "--Table-headerUnderlineThickness": "1px",
              "--TableRow-hoverBackground":
                "var(--joy-palette-background-level1)",
              "--TableCell-paddingY": "4px",
              "--TableCell-paddingX": "8px",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr style={{ textAlign: "center" }}>
                <th style={{ width: "5%", padding: "10px 6px" }}>#</th>
                <th style={{ width: "25%", padding: "10px 6px" }}>
                  Property Name
                </th>
                <th style={{ width: "55%", padding: "10px 6px" }}>
                  Property Value
                </th>
                <th style={{ width: "15%", padding: "10px 6px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPartnerPropertiesResult?.map((row, index) => (
                <tr key={index}>
                  <td>
                    <Typography level="body-sm">{index + 1}</Typography>
                  </td>
                  <td>
                    <Typography
                      level="body-sm"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {row.property_name}
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      level="body-sm"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {row.property_value}
                    </Typography>
                  </td>
                  <td>
                    <Button
                      variant="soft"
                      size="sm"
                      color="neutral"
                      onClick={() => {
                        setEditPropertyId(row.id);
                        setEditPropertyName(row.property_name);
                        setEditPropertyValue(row.property_value);
                        setEditPropertyModal(true);
                      }}
                    >
                      <Edit />
                    </Button>
                    <Button
                      variant="soft"
                      size="sm"
                      color="neutral"
                      onClick={() => downloadKey(row.id, row.property_name)}
                    >
                      <FileDownload />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      </Box>

      <Modal open={addPropertyModal}>
        <ModalDialog
          aria-labelledby="nested-modal-title"
          aria-describedby="nested-modal-description"
          sx={(theme) => ({
            maxWidth: 500,
            width: "90%",
            [theme.breakpoints.only("xs")]: {
              bottom: 0,
              left: 0,
              right: 0,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              transform: "none",
              maxWidth: "unset",
            },
          })}
        >
          <ModalClose
            variant="outlined"
            onClick={() => setAddPropertyModal(false)}
          />
          <DialogTitle>Add Property</DialogTitle>
          <DialogContent>Enter property details below.</DialogContent>
          <Stack
            component={"form"}
            onSubmit={handleAddProperty}
            gap={2}
            sx={{ mt: 2 }}
          >
            <FormControl required>
              <FormLabel>Property Name</FormLabel>

              <Select
                value={propertyName}
                onChange={(_, value) => setPropertyName(value ?? "")}
                placeholder="Select property"
                disabled={availablePropertyOptions.length === 0}
              >
                {availablePropertyOptions.length > 0 ? (
                  availablePropertyOptions.map((name) => (
                    <Option key={name} value={name}>
                      {name}
                    </Option>
                  ))
                ) : (
                  <Option value="" disabled>
                    All properties have already been added
                  </Option>
                )}
              </Select>
            </FormControl>
            <FormControl required>
              <FormLabel>Property Value</FormLabel>
              <Input
                type="text"
                value={propertyValue}
                onChange={(e) => setPropertyValue(e.target.value)}
                placeholder="Enter property value"
              />
            </FormControl>
            <Button
              type="submit"
              fullWidth
              disabled={availablePropertyOptions.length === 0}
            >
              ADD PROPERTY
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={editPropertyModal}>
        <ModalDialog
          aria-labelledby="nested-modal-title"
          aria-describedby="nested-modal-description"
          sx={(theme) => ({
            maxWidth: 500,
            width: "90%",
            [theme.breakpoints.only("xs")]: {
              bottom: 0,
              left: 0,
              right: 0,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              transform: "none",
              maxWidth: "unset",
            },
          })}
        >
          <ModalClose
            variant="outlined"
            onClick={() => setEditPropertyModal(false)}
          />
          <DialogTitle>Edit Property</DialogTitle>
          <DialogContent>Edit property details below.</DialogContent>
          <Stack
            component={"form"}
            onSubmit={handleEditProperty}
            gap={2}
            sx={{ mt: 2 }}
          >
            <FormControl required>
              <FormLabel>Property Name</FormLabel>
              <Select
                value={editPropertyName}
                onChange={(_, value) => setEditPropertyName(value ?? "")}
                placeholder="Select property"
              >
                {editPropertyOptions.map((name) => (
                  <Option key={name} value={name}>
                    {name}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <FormControl required>
              <FormLabel>Property Value</FormLabel>
              <Input
                type="text"
                value={editPropertyValue}
                onChange={(e) => setEditPropertyValue(e.target.value)}
                placeholder="Enter property value"
              />
            </FormControl>
            <Button type="submit" fullWidth>
              UPDATE PROPERTY
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}

const mapStateToProps = ({ api, auth }) => {
  const { otpResult: loginResult } = auth;
  const {
    viewApiStatus,
    viewApiResult,
    viewApiErrorMessage,

    getPartnerPropertiesStatus,
    getPartnerPropertiesResult,
    getPartnerPropertiesErrorMessage,

    createPartnerPropertyStatus,
    createPartnerPropertyResult,
    createPartnerPropertyErrorMessage,

    updatePartnerPropertyStatus,
    updatePartnerPropertyResult,
    updatePartnerPropertyErrorMessage,

    downloadKeyStatus,
    downloadKeyResult,
    downloadKeyErrorMessage,
  } = api;

  return {
    loginResult,

    viewApiStatus,
    viewApiResult,
    viewApiErrorMessage,

    getPartnerPropertiesStatus,
    getPartnerPropertiesResult,
    getPartnerPropertiesErrorMessage,

    createPartnerPropertyStatus,
    createPartnerPropertyResult,
    createPartnerPropertyErrorMessage,

    updatePartnerPropertyStatus,
    updatePartnerPropertyResult,
    updatePartnerPropertyErrorMessage,

    downloadKeyStatus,
    downloadKeyResult,
    downloadKeyErrorMessage,
  };
};

export default connect(mapStateToProps, {})(PartnerPropertiesPage);
